import {
  EditOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, MenuProps, Typography } from 'antd';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { queryClient } from 'src/queryClient';

import { programsApi } from '../api/programsApi';
import { ProgramStatus } from '../types/program';
import {
  showConfirmModal,
  showErrorModal,
  showSuccessModal,
} from '../utils/modalHelpers';

const { Text } = Typography;

interface ProgramsTableActionsProps {
  programId: number;
  programName: string;
  status: string;
}

export const ProgramsTableActions: FC<ProgramsTableActionsProps> = ({
  programId,
  programName,
  status,
}) => {
  const navigate = useNavigate();

  const handleEditProgram = async () => {
    navigate(`/programs/v2/create-or-update/${programId}`);
  };

  const handleInactiveProgram = async () => {
    try {
      await programsApi.unpublishProgram(programId);
      showSuccessModal({
        title: 'Sucesso',
        content: `Programa ${programName} desativado com sucesso!`,
        icon: <InfoCircleOutlined />,
        afterClose: () => {
          queryClient.invalidateQueries({ queryKey: ['program-list'] });
          queryClient.resetQueries({ queryKey: ['program', programId] });
        },
      });
    } catch (error) {
      showErrorModal({
        title: 'Tivemos um problema...',
        content: 'Não foi possível desativar o programa, tente novamente.',
      });
    }
  };

  const handleActiveProgram = async () => {
    try {
      await programsApi.publishProgram(programId);
      showSuccessModal({
        title: 'Sucesso',
        content: `Programa ${programName} ativado com sucesso!`,
        icon: <InfoCircleOutlined />,
        afterClose: () => {
          queryClient.invalidateQueries({ queryKey: ['program-list'] });
          queryClient.resetQueries({ queryKey: ['program', programId] });
        },
      });
    } catch (error) {
      showErrorModal({
        title: 'Tivemos um problema...',
        content: 'Não foi possível ativar o programa, tente novamente.',
      });
    }
  };

  const confirmInactiveProgram = async () => {
    showConfirmModal({
      title: `Desativar ${programName}`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Desativar programa',
      cancelText: 'Cancelar',
      content: (
        <Text>
          Ao desativar este programa, ele será despublicado e deixará de ser
          exibida para todos os usuários. Tem certeza que deseja continuar?
        </Text>
      ),
      onOk: handleInactiveProgram,
    });
  };
  const confirmActiveProgram = async () => {
    showConfirmModal({
      title: `Deseja realmente publicar novamente o programa: ${programName}`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Publicar programa',
      cancelText: 'Cancelar',
      content: (
        <Text>
          Ao ativá-lo, ficará disponível para todos os usuários. Tem certeza de
          que deseja continuar?
        </Text>
      ),
      onOk: handleActiveProgram,
    });
  };

  const handleClick: MenuProps['onClick'] = event => {
    switch (event.key) {
      case 'EDIT':
        handleEditProgram();
        break;
      case 'INACTIVE':
        confirmInactiveProgram();
        break;
      case 'ACTIVE':
        confirmActiveProgram();
        break;
      default:
        break;
    }
  };

  const items: MenuProps['items'] = [
    {
      key: 'EDIT',
      label: 'Editar',
      icon: <EditOutlined />,
    },
    status === ProgramStatus.Active
      ? {
          key: 'INACTIVE',
          label: `Desativar`,
          icon: <EyeInvisibleOutlined />,
        }
      : null,
    status === ProgramStatus.Inactive
      ? {
          key: 'ACTIVE',
          label: `Ativar`,
          icon: <EyeOutlined />,
        }
      : null,
  ];

  return (
    <Dropdown
      placement='bottomRight'
      menu={{ items, onClick: handleClick }}
      trigger={['click']}
    >
      <CustomButton
        data-testid='program-menu-actions'
        style={{ border: 'none' }}
      >
        <MoreOutlined />
      </CustomButton>
    </Dropdown>
  );
};

const CustomButton = styled(Button)`
  &:hover {
    background-color: rgba(62, 62, 62, 0.7);
    color: white;
  }
`;
